import { DomainList } from "@/components/domain";
import { DraggableList } from "@/components/draggable";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetTourDays } from "@/hooks/tour-days";
import { useGetTour } from "@/hooks/tours";
import { T } from "@/i18n";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarDays, GripVertical, Plus, Waypoints } from "lucide-react";
import { Day, useTourDaysContext } from "./-components/context";
import { TourDayDetails } from "./-components/day";
import { TourDaysProvider } from "./-components/provider";
import { createPortal } from "react-dom";
import { useMemo } from "react";
import { useGetTourServices } from "@/hooks/tour-services";
import { useGetTourAccommodations } from "@/hooks/tour-accommodations";

const NEW_DAY_BUTTON_ID = "tour-days-new-container";

export const Route = createFileRoute("/(tour-days)/tour-days/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data: tour, isLoading: tourLoading } = useGetTour(id);
  const { data: days, isLoading: daysLoading } = useGetTourDays(id);
  const { data: tourServices, isLoading: tourServicesLoading } =
    useGetTourServices(id);
  const { data: tourAccommodations, isLoading: tourAccommodationsLoading } =
    useGetTourAccommodations(id);

  const isLoading = useMemo(
    () => daysLoading || tourServicesLoading || tourAccommodationsLoading,
    [daysLoading, tourServicesLoading, tourAccommodationsLoading],
  );

  return (
    <DomainList
      type="list"
      domain="tourDays"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="tours.title" /> <Waypoints width={16} height={16} />
            </>
          ),
          href: "/tours",
        },
        {
          content: (
            <>
              {tourLoading ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <>
                  {tour?.name ?? "-"}
                  <Waypoints width={16} height={16} />
                </>
              )}
            </>
          ),
          href: "/tours/$id",
          params: { id },
        },
        {
          content: (
            <>
              <T tKey="tourDays.title" />{" "}
              <CalendarDays width={16} height={16} />
            </>
          ),
          href: "/tour-days/$id",
          params: { id },
        },
      ]}
      data={[]}
      isLoading={isLoading}
      hideNavActions
      newLink="/"
      navLeading={<div id={NEW_DAY_BUTTON_ID}></div>}
      renderItem={() => <></>}
    >
      {isLoading ? (
        <></>
      ) : (
        <TourDaysProvider
          tourId={id}
          tourDays={days ?? []}
          services={tourServices ?? []}
          accommodations={tourAccommodations ?? []}
        >
          <TourDays />
        </TourDaysProvider>
      )}
    </DomainList>
  );
}

function TourDays() {
  const { days, addNewDay, reorderDays } = useTourDaysContext();
  return (
    <>
      <DraggableList
        list={days}
        getId={(d) => d.tourDay.value.id.toString()}
        onChange={(list) => {
          reorderDays(list.map((d) => d.tourDay.value.day));
        }}
        render={(d) => <DayComponent day={d} key={d.tourDay.value.id} />}
      />
      {createPortal(
        <Button
          onClick={addNewDay}
          className="mt-8 flex h-full cursor-pointer items-center justify-center gap-2 hover:no-underline"
        >
          <Plus />
          <T tKey="tourDays.day.new" />
        </Button>,
        document.getElementById(NEW_DAY_BUTTON_ID)!,
      )}
    </>
  );
}

function DayComponent({ day }: { day: Day }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: day.tourDay.value.id.toString() });

  return (
    <div
      className="flex items-start gap-2"
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
      }}
    >
      <Button
        type="button"
        className="mt-2 cursor-grab"
        variant="ghost"
        {...attributes}
        {...listeners}
      >
        <GripVertical />
      </Button>
      <Accordion className="w-full" type="single" collapsible>
        <AccordionItem value={`day-${day.tourDay.value.day}-content`}>
          <AccordionTrigger className="w-full">
            Day {day.tourDay.value.day} {day.tourDay.value.name ?? "-"}
          </AccordionTrigger>
          <AccordionContent>
            <TourDayDetails day={day} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
